import { useMemo, useState } from 'react';
import { Divider, Skeleton, Box } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { useItemTooltip } from '@mui/x-charts';

import NoDataChart from 'src/components/chart/NoDataChart';
import { ChartPlaceholder } from 'src/components/chart/NoDataPlaceholder';
import { formatNumber } from 'src/utils/numberFormatter';
import { ImpactStats } from 'src/resources/api/reports/types.api';
import { AppGridContent, AppSection, AppTypography, StackRow } from 'src/components';
import { TooltipContainer, TooltipRow } from 'src/components/chart/ChartTooltip';

//
//

const CustomItemTooltipContent = (items: any[]) => {
  const tooltipData = useItemTooltip();
  if (!tooltipData) return null;

  const current = items[tooltipData.identifier.dataIndex as number];

  return (
    <TooltipContainer>
      <TooltipRow label={current?.key} currentItem={{ color: tooltipData?.color }} showDot />
      <Divider sx={{ my: 1 }} />
      <TooltipRow label="CO₂ amount:" value={(formatNumber(current?.co2Kgs)) + ' kg'} />
      <TooltipRow label="Number of contributions:" value={formatNumber(current?.numberOfContributions)} />
    </TooltipContainer>
  );
};

//

const SWITCH_TO_TONNES_AT = 1_000_000;

const getGreatestValue = (data: any, key: string) => data.map((d: any) => d[key] || 0).reduce((a:number,b:number) => Math.max(a,b),0)

const getChartCommonSetting = (useTonnes: boolean) => ({
  height: 300,
  colors: ['#2F795E'],
  slotProps: {
    tooltip: { trigger: 'item' as const },
    legend: {
      position: { vertical: 'bottom' as const, horizontal: 'middle' as const },
      sx: {
        fontSize: 12,
      },
    } as any,
  },
  yAxis: [{ width: 60 }],
  leftAxis: {
    axisId: 'DEFAULT_Y_AXIS_KEY',
    valueFormatter: (value: number) => `${(value / (useTonnes ? 1000 : 1)).toLocaleString()}`,
      tickLabelStyle: {
      fontSize: '10px',
    }
  }
});

interface Props {
  dataset?: ImpactStats;
  previousImpact?: {
    co2Kgs: number | null;
    numberOfContributions: number;
  } | null;
  showLineChart?: boolean;
  showBarChart?: boolean;
}

const CustomLegend = ({ chartData }: any) => {
  return <StackRow gap={1}>
    <Box width={15} height={15} sx={{ background: '#2f795e', borderRadius: '2px' }} />
    <AppTypography variant='body3'>CO₂ amount ({ getGreatestValue(chartData, 'co2Kgs') > SWITCH_TO_TONNES_AT ? 'T' : 'kg' })</AppTypography>
  </StackRow>
}

const ImpactGraphs: React.FC<Props> = ({ dataset, previousImpact, showLineChart, showBarChart }) => {
  const [isDatasetLoading, setIsDatasetLoading] = useState(true);

  const chartData = useMemo(() => {
    let data: any[] = [];

    if (dataset?.rows && dataset?.rows.length > 0) {
      data = dataset?.rows.map((row) => {
        const key = row.dimensionValues[0].value;
        const co2Kgs = +row.metricValues[0].value;
        const numberOfContributions = +row.metricValues[1].value;
        return { key, co2Kgs, numberOfContributions };
      });
    } else {
      data = [{ key: 'no data', co2Kgs: null, numberOfContributions: 0 }];
    }

    return data;
  }, [dataset?.rows]);

  const accDataset = useMemo(() => {

    const data: {co2Kgs: number | null; numberOfContributions: number}[] = JSON.parse(JSON.stringify(chartData));

    if(data.length > 0 && data && data[0] && previousImpact) {
      if(data[0].co2Kgs != null) {
        data[0].co2Kgs += previousImpact.co2Kgs as number;
      } else {
        data[0].co2Kgs = previousImpact.co2Kgs as number;
      }
      data[0].numberOfContributions += previousImpact.numberOfContributions as number;
    }

    data.forEach((record, index) => {
        if(index > 0) {
          if(record.co2Kgs != null) {
            record.co2Kgs += data[index - 1].co2Kgs as number;
          } else {
            record.co2Kgs = data[index - 1].co2Kgs;
          }
          record.numberOfContributions += data[index - 1].numberOfContributions;
        }
    });

    if (chartData?.some((el) => el.co2Kgs)) {
      setIsDatasetLoading(false);
    } else {
      setTimeout(() => {
        setIsDatasetLoading(false); // gracefully set loading to false if there is still empty array after 5 sec
      }, 5000);
    }

    return data;
  }, [chartData, previousImpact]);

  const hasDataset = useMemo(() => chartData?.some((el) => el.co2Kgs), [chartData]);
  const hasAccDataset = useMemo(() => accDataset?.some((el) => el.co2Kgs), [accDataset]);

  const noDataOverTimeText = 'No carbon offsets processed for the selected period. Check in again later or select different dates.';
  const noDataCumulativeText = 'Watch your impact grow here once your programme kicks off.';

  const barChartGraph = (
    <>
      {hasDataset && !isDatasetLoading && (
        <BarChart
          dataset={chartData}
          xAxis={[{ scaleType: 'band', dataKey: 'key' }]}
          series={[
            {
              stack: 'total',
              type: 'bar',
              dataKey: 'co2Kgs',
              label: 'CO₂ amount',
            },
            {
              stack: 'total',
              dataKey: 'numberOfContributions',
              label: 'Number of contributions',
            },
          ]}
          layout="vertical"
          sx={{ width: '100%' }}
          slots={{
            tooltip: () => CustomItemTooltipContent(chartData),
            legend: () => <CustomLegend chartData={chartData} />
          }}
          {...getChartCommonSetting(getGreatestValue(chartData, 'co2Kgs') > SWITCH_TO_TONNES_AT)}
        />
      )}
      {isDatasetLoading && (
        <Skeleton variant="rectangular" sx={{ width: 1, height: 340, borderRadius: 0.5 }} />
      )}
      {!hasDataset && !isDatasetLoading && (
        <NoDataChart text={noDataOverTimeText} Placeholder={ChartPlaceholder} />
      )}
    </>
  );

  const lineChartGraph = (
    <>
      {hasAccDataset && !isDatasetLoading && (
        <LineChart
          dataset={accDataset}
          xAxis={[{ scaleType: 'point', dataKey: 'key' }]}
          series={[
            {
              stack: 'total',
              dataKey: 'co2Kgs',
              label: 'CO₂ amount',
              showMark: (val: any) => val !== null,
              area: true,
              labelMarkType: 'square'
            },
            {
              stack: 'total',
              dataKey: 'numberOfContributions',
              label: 'Number of contributions',
              showMark: false,
            },
          ]}
          sx={{ width: '100%' }}
          slots={{
            tooltip: () => CustomItemTooltipContent(accDataset),
            legend: () => <CustomLegend chartData={chartData} />
          }}
          {...getChartCommonSetting(getGreatestValue(accDataset, 'co2Kgs') > SWITCH_TO_TONNES_AT)}
        />
      )}
      {isDatasetLoading && (
        <Skeleton variant="rectangular" sx={{ width: 1, height: 340, borderRadius: 0.5 }} />
      )}
      {!hasAccDataset && !isDatasetLoading && (
        <NoDataChart text={noDataCumulativeText} Placeholder={ChartPlaceholder} sx={{ flex: 1 }} />
      )}
    </>
  );

  if (showLineChart) {
    return lineChartGraph;
  }

  if (showBarChart) {
    return barChartGraph;
  }

  return (
    <>
      <AppGridContent size={{ xs: 12, md: 6 }}>
        <AppSection
          title="Carbon offset over time"
          sx={{ minHeight: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          {barChartGraph}
        </AppSection>
      </AppGridContent>

      <AppGridContent size={{ xs: 12, md: 6 }}>
        <AppSection
          title="Cumulative impact"
          sx={{ minHeight: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          {lineChartGraph}
        </AppSection>
      </AppGridContent>
    </>
  );
};

export default ImpactGraphs;
